import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '../Utils/SessionContext';
import TalentProfile from '../TalentComponents/TalentProfile';
// import TalentProfileAssessment from '../TalentComponents/TalentProfileAssessment'; // COMMENTED OUT - functionality moved to TalentReport
import TalentEvals from '../TalentComponents/TalentEvals';
import TalentReport from '../TalentComponents/TalentReport';
import TalentStatus from '../TalentComponents/Utils/TalentStatus';
import { API_ENDPOINTS, BASE_URL } from '../Utils/ApiConfig';
import Navigation from './Components/Navigation';
import toast from 'react-hot-toast';

const TalentDetails = () => {
  // Basic state variables
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const contentRef = useRef(null);
  
  // Dedicated state variable for talent data
  const [talentData, setTalentData] = useState(null);
  const [isCallbackLoading, setIsCallbackLoading] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useSession();

  // Extract talent ID from URL query parameters
  const getTalentId = () => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('tid');
  };

  const fetchTalentDetails = async () => {
    const talentId = getTalentId();
    
    if (!talentId) {
      setError('Talent ID is missing');
      setLoading(false);
      return;
    }

    try {
      // Fetch talent data from the webhook using GET request
      const url = new URL(API_ENDPOINTS.GET_TALENT_DATA);
      url.searchParams.append('tid', talentId);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.access_token}`
        }
      });
      
      if (!response.ok) {
        // Create an error with the status code attached so we can check for 401 in the catch block
        const error = new Error(`API request failed with status ${response.status}`);
        error.status = response.status; // Add status to the error object for use in catch block
        throw error;
      }
      
      const responseData = await response.json();
      console.log('API Response:', responseData);
      
      // Based on the feedback, the response is an array with a single item
      if (Array.isArray(responseData) && responseData.length > 0) {
        const data = responseData[0]; // Get the first item in the array
        
        // Store all talent data from the server response
        setTalentData({
          id: talentId,
          ...data // Include all data from the server response
        });
        
        console.log('Full talent data loaded:', data);

      } else {
        console.warn('Unexpected API response format:', responseData);
        setError('Unexpected response format from the server');
      }
    } catch (err) {
      console.error('Error fetching talent data:', err);
      console.log('Error status code:', err.status);
      
      // Use the status property we added to the error object to check for 401 (Unauthorized)
      if (err.status === 401) {
        console.log('Session expired or unauthorized. Redirecting to login page.');
        logout();
        navigate('/login');
        return;
      }
      
      setError(`Failed to load talent data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Reload function to refresh talent data
  const reloadTalentData = async () => {
    setLoading(true);
    setError(null);
    await fetchTalentDetails();
  };

  useEffect(() => {
    fetchTalentDetails();
  }, [location.search]);
  
  // Check for hash fragment in URL and set active tab accordingly
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && ['profile', 'assessment', 'evaluations', 'report'].includes(hash)) {
      setActiveTab(hash);
    }
  }, [location.hash]);
  
  // Set document title to talent name
  useEffect(() => {
    if (talentData && talentData.fname) {
      document.title = "Talent | " + talentData.fname + " " + talentData.lname;
    } else {
      document.title = "Talent Details";
    }
    
    // Reset title when component unmounts
    return () => {
      document.title = "VaaS Admin";
    };
  }, [talentData]);

  
  const handleTabChange = (tabName) => {
    // First update the active tab
    setActiveTab(tabName);
    
    // Update URL with hash fragment
    navigate(`${location.pathname}${location.search}#${tabName}`, { replace: true });
    
    // Then scroll the window to the top
    window.scrollTo(0, 0);
    
    // For good measure, also scroll the content container to the top
    // This is done with a small delay to ensure the DOM has updated
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
    }, 10);
  };

  // Handle callback trigger
  const handleCallbackTrigger = async () => {
    const talentId = getTalentId();
    if (!talentId) return;

    const confirmed = window.confirm("Are you sure you want to trigger the Talent Assessment callback?");
    if (!confirmed) return;

    setIsCallbackLoading(true);
    
    try {
      const response = await fetch(API_ENDPOINTS.CALL_CALLBACK, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.access_token}`
        },
        body: JSON.stringify({
          talent_id: talentId,
          uid: user.id
        })
      });

      if (!response.ok) {
        const error = new Error(`Error: ${response.status} ${response.statusText}`);
        error.status = response.status;
        throw error;
      }

      toast.success('Callback triggered successfully! Please note that this only sends the last created/assigned evaluation.');
    } catch (err) {
      console.error('Error triggering callback:', err);
      if (err.status === 401) {
        console.log('Session expired or unauthorized. Redirecting to login page.');
        logout();
        navigate('/login');
        return;
      }
      toast.error('Failed to trigger callback. Please try again.');
    } finally {
      setIsCallbackLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-100 p-5 pt-[76px]"> {/* Added top padding to account for fixed header */}
      <Navigation 
        title={
          loading ? (
            <span className="inline-flex items-center">
              <span className="animate-pulse">Loading talent details...</span>
            </span>
          ) : talentData ? (
            <span>
              {talentData.fname + " " + talentData.lname}
            </span>
          ) : (
            "Talent Details"
          )
        } 
      />
      
      <div className="bg-white rounded-lg shadow relative">
        {loading ? (
          <div className="text-center py-8">
            <p className="text-gray-600">Loading talent details...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-500">{error}</p>
            <p className="text-gray-600 mt-2">Please try again or go back to the talent page.</p>
          </div>
        ) : (
          <div>
            {/* Tabs Navigation - Made sticky */}
            <div className="sticky top-[76px] z-30 bg-white border-b border-gray-200">
              <div className="flex justify-between items-center">
                <nav className="flex -mb-px overflow-x-auto">
                <button
                  onClick={() => handleTabChange('profile')}
                  className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                    activeTab === 'profile'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Talent Profile
                </button>
                {/* COMMENTED OUT - Profile Assessment tab - functionality moved to Report tab
                <button
                  onClick={() => handleTabChange('assessment')}
                  className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                    activeTab === 'assessment'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Profile Assessment
                </button>
                */}
                <button
                  onClick={() => handleTabChange('evaluations')}
                  className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                    activeTab === 'evaluations'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  All Talent Evaluations
                </button>
                <button
                  onClick={() => handleTabChange('report')}
                  className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                    activeTab === 'report'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Talent Report
                </button>

                </nav>
                <div className="flex flex-wrap gap-2 items-center pr-4">
                  <div className="flex flex-wrap items-center">
                    {getTalentId() && (
                      <span className="text-xs text-gray-500">
                        Talent ID: {getTalentId()}
                      </span>
                    )}
                  </div>
                  {talentData?.status && (
                    <div className="ml-auto flex items-center gap-2">
                      <TalentStatus status={talentData.status} showDot={true} size="sm" />
                      <button
                        onClick={handleCallbackTrigger}
                        disabled={isCallbackLoading}
                        className="p-1 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Trigger callback"
                      >
                        {isCallbackLoading ? (
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : (
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Tab Content - Using key to force remount when tab changes */}
            <div className="p-6 overflow-auto" ref={contentRef} key={activeTab}>
              {activeTab === 'profile' && (
                <TalentProfile talent={talentData} />
              )}
              
              {/* COMMENTED OUT - Profile Assessment tab content - functionality moved to Report tab
              {activeTab === 'assessment' && (
                <TalentProfileAssessment talent={talentData} />
              )}
              */}
              
              {activeTab === 'evaluations' && (
                <TalentEvals talent={talentData} />
              )}
              
              {activeTab === 'report' && (
                <TalentReport talent={talentData} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TalentDetails;
